import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { AdminRangeVars, AdminTimeRange } from "../graphql/types";
import { dayEndExclusiveIsoUtc, dayStartIsoUtc } from "../utils/format";

/**
 * Cross-cutting UI controls that live in the Topbar but drive the routed
 * screens: the Tag/Woche/Monat time-range toggle + custom Von/Bis range feed
 * the Dashboard queries; `search` feeds the Users table. In the prototype
 * these were fields on one logic class — here they're a small context so the
 * Topbar can set them and the screens can read them.
 *
 * `timeRange`/`customFrom`/`customTo` map straight onto the §6.1 range query
 * variables via `rangeVars`. `search` is raw here; screens debounce as needed.
 */
export interface AdminControls {
  timeRange: AdminTimeRange;
  /** ISO date (yyyy-mm-dd); only meaningful when timeRange === "CUSTOM". */
  customFrom: string | null;
  customTo: string | null;
  search: string;

  /** Set a preset range (DAY/WEEK/MONTH). Clears any custom range. */
  setPresetRange: (range: AdminTimeRange) => void;
  /** Apply a custom Von/Bis span → timeRange becomes "CUSTOM". */
  applyCustomRange: (from: string, to: string) => void;
  /** Clear the custom span and fall back to WEEK (prototype default). */
  clearCustomRange: () => void;
  setSearch: (value: string) => void;

  /** Derived query variables for the §6.1 range queries. */
  rangeVars: AdminRangeVars;
}

const AdminControlsContext = createContext<AdminControls | null>(null);

export const AdminControlsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [timeRange, setTimeRange] = useState<AdminTimeRange>("WEEK");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const setPresetRange = useCallback((range: AdminTimeRange) => {
    setTimeRange(range);
    setCustomFrom(null);
    setCustomTo(null);
  }, []);

  const applyCustomRange = useCallback((from: string, to: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    setTimeRange("CUSTOM");
  }, []);

  const clearCustomRange = useCallback(() => {
    setCustomFrom(null);
    setCustomTo(null);
    setTimeRange("WEEK");
  }, []);

  const rangeVars = useMemo<AdminRangeVars>(() => {
    // For a custom span, normalize the date-only picker values to full UTC ISO
    // timestamps and make "Bis" inclusive by sending the next day as the
    // exclusive upper bound (backend window is half-open `[from, to)`).
    if (timeRange === "CUSTOM" && customFrom && customTo) {
      return {
        range: "CUSTOM",
        from: dayStartIsoUtc(customFrom),
        to: dayEndExclusiveIsoUtc(customTo),
      };
    }

    // Presets are rolling windows ending "now": Tag = last 24h, Woche = last
    // 7 days, Monat = last calendar month. The backend derives the equal-length
    // preceding window from these bounds for the trend comparison.
    const now = new Date();
    const from = new Date(now);
    switch (timeRange) {
      case "DAY":
        from.setUTCHours(from.getUTCHours() - 24);
        break;
      case "MONTH": {
        // One calendar month back, clamping the day so e.g. 31 Mar → 28 Feb
        // instead of JS's default roll-forward into March.
        const y = now.getUTCFullYear();
        const m = now.getUTCMonth();
        // Day 0 of month `m` = last day of the previous month → its day count.
        const daysInPrevMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
        from.setUTCFullYear(y, m - 1, Math.min(now.getUTCDate(), daysInPrevMonth));
        break;
      }
      case "WEEK":
      default:
        from.setUTCDate(from.getUTCDate() - 7);
        break;
    }
    return { range: timeRange, from: from.toISOString(), to: now.toISOString() };
  }, [timeRange, customFrom, customTo]);

  const value = useMemo<AdminControls>(
    () => ({
      timeRange,
      customFrom,
      customTo,
      search,
      setPresetRange,
      applyCustomRange,
      clearCustomRange,
      setSearch,
      rangeVars,
    }),
    [
      timeRange,
      customFrom,
      customTo,
      search,
      setPresetRange,
      applyCustomRange,
      clearCustomRange,
      rangeVars,
    ],
  );

  return (
    <AdminControlsContext.Provider value={value}>{children}</AdminControlsContext.Provider>
  );
};

export function useAdminControls(): AdminControls {
  const ctx = useContext(AdminControlsContext);
  if (!ctx) {
    throw new Error("useAdminControls must be used within an AdminControlsProvider");
  }
  return ctx;
}
